
<!--
  - Copyright (c) 2021 by Leyton Group - All Rights Reserved.
  -  This source code, and resulting software, is the confidential and proprietary information
  -  ("Proprietary Information") and is the intellectual property ("Intellectual Property")
  -  of Leyton Group, Inc. ("The Company"). You shall not disclose such Proprietary Information
  -  and shall use it only in accordance with the terms and conditions of any and all license
  -  agreements you have entered into with The Company.
  -->

<template>
  <div id="SimulationsDetail">
    <div
      class="d-flex flex-column align-items-start flex-md-row align-items-md-center min-h"
    >
      <h2 class="text-primary mb-2 mr-3 font-40px">{{ this.edit ? this.simulation.name : $t("perform_new_simulation") }}</h2>
    </div>

    {{simulationForm}}

    <collapse-card id="colpase-general_informations" visible :noButton="true" :noTitlePrimary="true" >
      <template v-slot:title>
        {{ $t("general_informations") }}
      </template>
      <template v-slot:body>
        <b-form-row no-gutters class="mt-4 c-form align-items-end">
          <b-col cols="12" sm="8">
            <b-form-group
              :label="$t('simulation_name')"
              label-for="name"
              label-class="fsize-1"
            >
              <b-form-input
                :placeholder="$t('simulation_name')"
                id="name"
                type="text"
                pill
                v-model="$v.simulationForm.name.$model"
              ></b-form-input>
              <div v-if="$v.simulationForm.name.$anyDirty && $v.simulationForm.name.$invalid">
                <small class="form-text text-danger" v-if="!$v.simulationForm.name.required">
                  {{$t('required_field')}}
                </small>
              </div>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row no-gutters class="mt-4 c-form align-items-end">
          <b-col cols="12" sm="6" lg="4" xl="3">
            <b-form-group
              :label="$t('estimated_end_date')"
              label-for="estimated_end_date"
              label-class="fsize-1"
            >
            <date-picker 
              id="estimated_end_date"
              v-model="$v.simulationForm.deliveryDate.$model"
              format='D MMMM YYYY' 
              valueType="YYYY-MM-DD"
              prefix-class="xmx" 
              :placeholder="$t('estimated_end_date')"
              :clearable="false"
              :editable = "false"
              class="big-input" >
                <template slot="icon-calendar"> 
                    <b-icon icon="calendar3"></b-icon>       
                </template>
           </date-picker>
              <div v-if="$v.simulationForm.deliveryDate.$anyDirty && $v.simulationForm.deliveryDate.$invalid">
                <small class="form-text text-danger" v-if="!$v.simulationForm.deliveryDate.required">
                  {{$t('required_field')}}
                </small>
              </div>
            </b-form-group>
          </b-col>
        </b-form-row>
        <b-form-row no-gutters class="mt-4 c-form align-items-end" v-if="!isEdit">
          <b-col cols="12">
            <b-form-group>
              <b-form-radio-group
                      id="radio-group-1"
                      v-model="$v.simulationForm.simulationType.$model"
                      :options="simulationTypes"
                      name="radio-options"
              >
                <div v-if="$v.simulationForm.simulationType.$anyDirty && $v.simulationForm.simulationType.$invalid">
                  <small class="form-text text-danger" v-if="!$v.simulationForm.simulationType.required">
                    {{$t('required_field')}}
                  </small>
                </div>
              </b-form-radio-group>
            </b-form-group>
          </b-col>
        </b-form-row>
      </template>
    </collapse-card>
    <div
        v-if="loading && simulationForm.simulationType !== 'COMPLEX'"
        class="d-flex align-items-center justify-content-center view-area mb-4 height-doc"
    >
      <b-spinner variant="primary" type="grow" label="Spinning"></b-spinner>
    </div>
    <b-row class="documents" align-v="stretch" v-if="this.simulation.id && simulationForm.simulationType !== 'COMPLEX'" v-show="!loading">
      <b-col cols="12" xl="8" class="mb-4">
        <b-card class="h-100 border-raduis-xl border-0 box-shadow-sm p-3">
          <h4 class="mb-4">{{ $t("documents") }}</h4>
          <div class="mb-4">
            <documents-actions
              :documents="getDocuments"
              :selectDocuments="selectDocuments"
              :noFilter="true"
              :noSearch="true"
              :isSimulation="true"
              v-on:download="download()"
              :queryConfig="queryConfig"
              @applyFilter="applyFilter($event)"
              @reloadData="applyFilter($event)"
            ></documents-actions>
          </div>
          <documents-list
            :documents="getDocuments"
            :selectDocuments="selectDocuments"
            v-on:selectDocument="selectDocument($event)"
          ></documents-list>
        </b-card>
      </b-col>
      <b-col cols="12" xl="4" class="mb-4">
        <documents-details
          :documents="getDocuments"
          :selectDocuments="selectDocuments"
        ></documents-details>
      </b-col>
    </b-row>
    <b-row>
      <b-col cols="12">
        <b-card
          class="border-raduis-xl border-0 box-shadow-sm text-center"
          body-class="p-3"
        >
          <b-row>
            <b-col cols="12" sm="6" class="text-sm-right mb-2 mb-sm-0">
              <b-button 
               :to="{ name: 'Simulations' }"
                pill
                variant="outline-primary"
                class="py-2 px-5"
              >
                <span>{{ $t("cancel") }}</span>
              </b-button>
            </b-col>
            <b-col cols="12" sm="6" class="text-sm-left">
              <b-button
                      id="submit-form"
                      @click="persistIfSimpleOrOpenModal()"
                      :disabled="saveSimulationLoader || $v.simulationForm.$invalid"
                      pill
                      variant="primary"
                      class="py-2 px-5">
                <b-spinner variant="light" small
                  :class="simulationForm.simulationType !== 'COMPLEX' ? 'mx-4' : 'mx-3'"
                  class="align-middle"
                  v-if="saveSimulationLoader && simulationForm.simulationType === 'COMPLEX'">
                </b-spinner>
                <span v-if="!saveSimulationLoader || simulationForm.simulationType !== 'COMPLEX'"> {{simulationForm.simulationType !== 'COMPLEX' ? $t("save") : $t("next") }}</span>
              </b-button>
            </b-col>
          </b-row>
        </b-card>
      </b-col>
    </b-row>
    <confirm-modal
        id="confirm-simulation"
        :content="$t('would-you-like-to-save-estimations')"
        :loader="saveSimulationLoader"
        fontSize="h3"
        backdrop-close="false"
        @yesClick="modelConfirm()"
        @close="hideConfirmationModal()"
    ></confirm-modal>
  </div>
</template>

<script>
import {required} from 'vuelidate/lib/validators';
import CollapseCard from '../../components/static/CollapseCard.vue';
import DocumentsActions from '../../components/static/DocumentsActions.vue';
import DocumentsList from '../../components/static/DocumentsList.vue';
import DocumentsDetails from '../../components/static/DocumentsDetails.vue';
import {mapActions, mapGetters, mapMutations} from 'vuex';
import {workService} from '../Works/services';
import moment from 'moment';
import {documentService} from '../service/document.service';
import eventBus from '../../shared/event.bus';
import { find } from 'lodash';
import {queryConstant} from '../../shared/queryConstant';
import ConfirmModal from '@/components/static/ConfirmModal';

const CONFIRM_SIMULATION = 'confirm-simulation';

export default {
  components: {
    CollapseCard,
    DocumentsActions,
    DocumentsList,
    DocumentsDetails,
    ConfirmModal
  },
  name: 'AddEditSimulation',
  data() {
    return {
      edit: false,
      selectDocuments: [],
      simulationForm: {
        name: '',
        deliveryDate: '',
        simulationType: 'SIMPLE'
      },
      simulationTypes: [
        {
          text: 'J\'ai un document décrivant mon projet et je souhaite avoir une estimation sur cette base.',
          value: 'SIMPLE'
        },
        {
          text: 'J\'ai le détail de mon projet et je peux renseigner les informations nécessaires afin d\'avoir une estimation.',
          value: 'COMPLEX'
        },
      ],
      isComplexSimulation: false,
      customFilter: queryConstant.setDefaultCustomFilter(),
      queryConfig: queryConstant.DOCUMENT_CONFIG
    };
  },
  validations: {
    simulationForm: {
      name: {
        required
      },
      deliveryDate: {
        required
      },
      simulationType: {
        required
      }
    }
  },
  mounted() {
    this.getDocumentsTypesAction();
    if(this.$route.params['id']){
      // case of update
      this.edit = true;
      const id = this.$route.params['id']
      this.fetchSimulation(id);
    } else {
      // case of add
      this.$store.commit('document/setDocuments', []);
      this.resetSimulationAction();
    }
    eventBus.$on('reloadDocuments', (documentsIds) => {
      this.selectDocuments = []
      if (documentsIds) {
        this.$root.$emit('bv::hide::modal', 'addDocumet');
        eventBus.$emit('setCurrentStep_One');
        this.getDocumentsAction({ id: this.simulation.id, customFilter: this.customFilter });
      }
    })
  },
  computed: {
    ...mapGetters('document', ['getDocuments', 'getDocumentsTypes']),
    ...mapGetters('simulation', {simulation : 'getSimulation'}),
    ...mapGetters('loader', {loading: 'getLoading'}),
    ...mapGetters('loader', {saveSimulationLoader: 'getSaveSimulationLoader'}),
    isEdit() {
      return this.simulation.id !== null && this.simulation.id !== undefined && this.simulation.id !== '';
    }
  },
  methods: {
    ...mapActions('document', ['getDocumentsAction', 'getDocumentsTypesAction']),
    ...mapActions('notification', ['openModal', 'closeModal']),
    ...mapActions('simulation', ['persistSimulationAction','resetSimulationAction']),
    ...mapMutations('document', ['setSelectedDocuments']),

    persistIfSimpleOrOpenModal() {
      if (this.simulationForm.simulationType !== 'COMPLEX') {
        this.openModal({instance: this.$root, id: CONFIRM_SIMULATION})
      } else {
        this.persist()
      }
    },
    modelConfirm() {
      this.persist();
    },
    hideConfirmationModal() {
      this.closeModal({instance: this.$root, id: CONFIRM_SIMULATION})
    },
    persist(){
      let id = null;
      if(this.simulation.id){
        id = this.simulation.id;
      }
      const data = {
        id : id,
        name: this.simulationForm.name,
        deliveryDate: this.simulationForm.deliveryDate,
        type: this.simulationForm.simulationType
      };
      this.isComplexSimulation = data.type === 'COMPLEX'
      this.persistSimulationAction({simulation: data})
    },
    fetchSimulation(id) {
      workService.fetchWorkById(id).then(
          (result) => {
            this.$store.commit('simulation/setSimulation', result);
            this.$store.dispatch('document/getDocumentsAction', {id:id, customFilter: this.customFilter});
            this.simulationForm = {
              name: result.name,
              deliveryDate: moment(result.deliveryDate , 'x').format('YYYY-MM-DD'),
              simulationType: result.simulationType
            };
          },
          (error) => {
            console.log(error);
          }
      );
    },
    selectDocument(id){
      const index = this.selectDocuments.indexOf(id);
      if (index > -1) {
        this.selectDocuments.splice(index, 1);
      } else {
        this.selectDocuments.push(id);
      }
      const docs = []
      const allDocs = JSON.parse(JSON.stringify(this.getDocuments))
      this.selectDocuments.forEach(docId => {
        docs.push(find(allDocs, { 'id': docId}))
      })
      if (docs && docs.length) {
        this.mapDocumentTypes(docs)
      }
    },
    mapDocumentTypes(docs) {
      const documents = docs
      documents.forEach(doc => {
        const types = [];
        if (doc.types) {
          doc.types.forEach(type => {
            types.push(find(this.getDocumentsTypes, { 'code': type }))
          })
        }
        doc.types = types;
      })
      this.setSelectedDocuments(documents);
    },
    download(){
      documentService.downloadDocuments(this.selectDocuments);
    },
    applyFilter(event) {
      this.selectDocuments = [];
      this.customFilter = event.filter.custom;
      this.getDocumentsAction({ id: parseInt(this.$route.params['id'], 10), customFilter: this.customFilter });
    },
  },
  watch: {
    saveSimulationLoader(value) {
      if (value === false) {
        this.closeModal({instance: this.$root, id: CONFIRM_SIMULATION})
      }
    }
  },
  destroyed() {
    eventBus.$off('reloadDocuments');
  }
};
</script>

<style lang='scss'>
  .min-h {
    min-height: 48px;
  }
  .height-doc {
    min-height: 320px;
    max-height: 320px;
  }
  .custom-control-inline {
    display: flex !important;
  }
</style>
